import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PermissionEntityType } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterSearch } from '@/components/common/filters/FilterSearch'
import { TableCard } from '@/components/common/table/TableCard'
import { useAbsenceSummaries } from '../hooks/useAbsenceSummaries'
import { AbsenceOverviewDataTable } from './AbsenceOverviewDataTable'
import { AbsenceExportDialog } from './AbsenceExportDialog'
import { VacationQuotaDialog } from './VacationQuotaDialog'

interface Props {
  module: ServiceModuleModel
  /** Row click → employee view (view state lives in the page). */
  onRowClick?: (employee: EmployeeUserResponse) => void
}

/** Unwrap the query response from the axios wrapper if needed (hooks return the full response). */
function unwrap<T>(data: unknown): T | undefined {
  return ((data as { data?: T })?.data ?? data) as T | undefined
}

/**
 * "Smart" overview of the absence module: loads the employees + their metrics
 * (fan-out via `useAbsenceSummaries`), filters locally by name search and renders the
 * dumb `AbsenceOverviewDataTable`. Also holds the dialog state of the two
 * row buttons (current vacation / export) and the (for now empty) dialogs.
 */
export function AbsenceOverviewTable({ module, onRowClick }: Props) {
  const { t } = useTranslation(['service-modules-absence', 'filters'])
  const { userHasEntityPermission } = usePermissions()
  // Absence management (edit/create quota) depends on ServiceModules.Edit
  // of this module (global OR object-specific permission).
  const canEdit = userHasEntityPermission(PermissionEntityType.ServiceModules, 'Edit', module.id)
  const [quotaEmployee, setQuotaEmployee] = useState<EmployeeUserResponse | null>(null)
  const [exportEmployee, setExportEmployee] = useState<EmployeeUserResponse | null>(null)
  const [search, setSearch] = useState<string | undefined>()

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const filteredEmployees = useMemo(() => {
    const query = search?.trim().toLowerCase()
    if (!query) return employees
    return employees.filter((e) =>
      `${e.firstName ?? ''} ${e.lastName ?? ''}`.toLowerCase().includes(query),
    )
  }, [employees, search])

  const { summariesById } = useAbsenceSummaries(module.id, employees)

  return (
    <>
      <FilterRow>
        <FilterSearch
          search={search}
          onChange={setSearch}
          useDebounce={false}
          searchLabel={t('searchbar.searchEmployees', { ns: 'filters' })}
        />
      </FilterRow>

      <TableCard>
        <div className="flex-1 overflow-auto scrollbar-custom relative">
          <AbsenceOverviewDataTable
            employees={filteredEmployees}
            summariesById={summariesById}
            isLoading={employeesQuery.isLoading}
            isError={employeesQuery.isError}
            onRowClick={onRowClick}
            onShowQuota={setQuotaEmployee}
            onExport={setExportEmployee}
          />
        </div>
      </TableCard>

      {quotaEmployee && (
        <VacationQuotaDialog
          open
          onOpenChange={(open) => !open && setQuotaEmployee(null)}
          serviceModuleId={module.id}
          employee={quotaEmployee}
          canEdit={canEdit}
        />
      )}

      <AbsenceExportDialog
        open={!!exportEmployee}
        onOpenChange={(open) => !open && setExportEmployee(null)}
        serviceModuleId={module.id}
        employee={exportEmployee ?? undefined}
      />
    </>
  )
}
